import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import type { OrgChart as OrgChartData, OrgChartNode, OrgChartPerson } from '../../lib/peopleApi';
import { useSetPersonManager } from '../../hooks/usePeople';
import { initials } from './UtilizationBar';
import { Select } from '../ui/Select';

/** Minimal person shape passed to the remove-from-project handler (CLE-441). */
export interface OrgChartRemovablePerson {
  id: string;
  email: string;
  fullName: string;
}

interface OrgChartProps {
  data: OrgChartData;
  /** When true, admin edit affordances (drag/assign/detach) are enabled. */
  editable?: boolean;
  /**
   * When provided, each node shows a "remove" affordance that calls this with
   * the person. Used by the project team tab (CLE-441) so members can be taken
   * off the project directly from the org chart, not only from the list view.
   */
  onRemoveMember?: (person: OrgChartRemovablePerson) => void;
  /** Disables the remove buttons while a removal is in flight. */
  removePending?: boolean;
}

/**
 * Editable organisational chart (CLE-70).
 *
 * Renders the team as a top-down hierarchy built from each person's
 * `managerId`. Connector lines between a manager and its direct reports are
 * drawn with pure CSS borders (no external chart library), which keeps the
 * bundle small and the layout responsive — the chart scrolls horizontally on
 * narrow screens and supports zoom in/out and per-branch collapse/expand.
 *
 * Edit mode (admin only) lets you:
 *  - drag a person onto another to make them report to that person,
 *  - click a node to assign/detach its superior via a small menu,
 *  - detach a person back to the top level.
 *
 * Each structural change is persisted immediately via PATCH
 * /api/people/:id/manager (optimistic UX through react-query invalidation),
 * so there is no separate batch "Save" — the chart is always consistent with
 * the database. A dedicated "Done editing" toggle leaves edit mode.
 */
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 1.5;

export function OrgChart({
  data,
  editable = false,
  onRemoveMember,
  removePending = false,
}: OrgChartProps) {
  const [zoom, setZoom] = useState(1);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [dragId, setDragId] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Refs + natural (untransformed) content size, used for fit-to-view zoom and
  // for sizing the wrapper so the scaled chart doesn't leave phantom scrollbars.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);

  const setManager = useSetPersonManager();

  /**
   * Compute the zoom that makes the whole chart visible: fit the container
   * width and the remaining viewport height, capped at 100%. Uses the layout
   * (untransformed) size of the chart, which CSS transforms don't affect.
   */
  const fitToView = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;
    const w = content.offsetWidth;
    const h = content.offsetHeight;
    if (!w || !h) return;
    const cs = getComputedStyle(container);
    const availW = container.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    const availH = Math.max(
      240,
      window.innerHeight -
        container.getBoundingClientRect().top -
        parseFloat(cs.paddingTop) -
        parseFloat(cs.paddingBottom) -
        32,
    );
    const fit = Math.min(1, availW / w, availH / h);
    setZoom(Math.max(MIN_ZOOM, Math.floor(fit * 100) / 100));
  }, []);

  // Track the natural size of the chart so the wrapper below can shrink with
  // the scale (transforms don't change layout size on their own).
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const update = () => setNatural({ w: el.offsetWidth, h: el.offsetHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [data.roots.length]);

  // Default view: zoom out so the entire org chart is visible.
  useLayoutEffect(() => {
    fitToView();
  }, [data.roots, fitToView]);

  // Flat lookup of everyone in the chart, for the assign-manager picker.
  const allPeople = useMemo<OrgChartPerson[]>(() => {
    const out: OrgChartPerson[] = [];
    const walk = (nodes: OrgChartNode[]) => {
      for (const n of nodes) {
        out.push(n);
        walk(n.reports);
      }
    };
    walk(data.roots);
    return out.sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [data.roots]);

  const toggleCollapse = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Set of ids that are descendants of `id` (to forbid dropping a manager onto
  // their own report, which the backend also rejects as a cycle).
  const descendantsOf = useCallback(
    (id: string): Set<string> => {
      const result = new Set<string>();
      const find = (nodes: OrgChartNode[]): OrgChartNode | null => {
        for (const n of nodes) {
          if (n.id === id) return n;
          const inner = find(n.reports);
          if (inner) return inner;
        }
        return null;
      };
      const node = find(data.roots);
      const collect = (n: OrgChartNode) => {
        for (const r of n.reports) {
          result.add(r.id);
          collect(r);
        }
      };
      if (node) collect(node);
      return result;
    },
    [data.roots],
  );

  const assign = useCallback(
    (personId: string, managerId: string | null) => {
      setError(null);
      setMenuFor(null);
      if (personId === managerId) return;
      setManager.mutate(
        { id: personId, managerId },
        {
          onError: (e) =>
            setError(e instanceof Error ? e.message : 'Actualizarea organigramei a eșuat'),
        },
      );
    },
    [setManager],
  );

  const handleDrop = useCallback(
    (targetId: string) => {
      if (!dragId) return;
      const moving = dragId;
      setDragId(null);
      if (moving === targetId) return;
      // Forbid dropping onto your own subtree (would be a cycle).
      if (descendantsOf(moving).has(targetId)) {
        setError('Nu poți muta o persoană sub unul dintre subordonații săi.');
        return;
      }
      assign(moving, targetId);
    },
    [dragId, descendantsOf, assign],
  );

  const empty = data.roots.length === 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* Toolbar: zoom + edit hint */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(MIN_ZOOM, Math.round((z - 0.1) * 10) / 10))}
            className="rounded border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
            aria-label="Micșorează"
          >
            −
          </button>
          <span className="w-12 text-center text-xs text-gray-500">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(MAX_ZOOM, Math.round((z + 0.1) * 10) / 10))}
            className="rounded border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
            aria-label="Mărește"
          >
            +
          </button>
          <button
            type="button"
            onClick={fitToView}
            className="ml-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
          >
            Potrivește
          </button>
          <button
            type="button"
            onClick={() => setZoom(1)}
            className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
          >
            100%
          </button>
        </div>
        {editable && (
          <p className="text-xs text-gray-500">
            Trage o persoană peste alta pentru a-i seta managerul sau dă clic pe o fișă pentru a
            edita.
          </p>
        )}
      </div>

      {error && (
        <div className="mx-3 mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Scroll container — horizontal scroll on small screens. */}
      <div ref={containerRef} className="overflow-auto p-6">
        {empty ? (
          <p className="text-sm text-gray-500">
            Organigrama este goală.
            {editable
              ? ' Folosește modul de editare pentru a aloca manageri și a construi ierarhia.'
              : ' Un administrator o poate construi din modul de editare.'}
          </p>
        ) : (
          <div
            className="mx-auto"
            style={natural ? { width: natural.w * zoom, height: natural.h * zoom } : undefined}
          >
            <div
              ref={contentRef}
              className="inline-block origin-top-left transition-transform"
              style={{ transform: `scale(${zoom})` }}
            >
              <ul className="org-tree flex justify-center gap-8">
                {data.roots.map((node) => (
                  <OrgNode
                    key={node.id}
                    node={node}
                    editable={editable}
                    collapsed={collapsed}
                    onToggle={toggleCollapse}
                    dragId={dragId}
                    onDragStart={setDragId}
                    onDragEnd={() => setDragId(null)}
                    onDrop={handleDrop}
                    menuFor={menuFor}
                    onOpenMenu={setMenuFor}
                    onAssign={assign}
                    allPeople={allPeople}
                    onRemoveMember={onRemoveMember}
                    removePending={removePending}
                  />
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Unassigned members tray — admins place these into the chart. */}
      {editable && data.unassigned.length > 0 && (
        <div className="border-t border-gray-100 px-4 py-3">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            Nealocați ({data.unassigned.length}) — trage peste un manager sau folosește meniul fișei
          </p>
          <div className="flex flex-wrap gap-2">
            {data.unassigned.map((p) => (
              <div
                key={p.id}
                draggable
                onDragStart={() => setDragId(p.id)}
                onDragEnd={() => setDragId(null)}
                className="flex cursor-grab items-center gap-2 rounded-full border border-gray-200 bg-gray-50 py-1 pl-1 pr-3 active:cursor-grabbing"
                title={`${p.fullName}${p.title ? ` — ${p.title}` : ''}`}
              >
                <Avatar person={p} size="sm" />
                <span className="text-sm text-gray-700">{p.fullName}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface OrgNodeProps {
  node: OrgChartNode;
  editable: boolean;
  collapsed: Set<string>;
  onToggle: (id: string) => void;
  dragId: string | null;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onDrop: (targetId: string) => void;
  menuFor: string | null;
  onOpenMenu: (id: string | null) => void;
  onAssign: (personId: string, managerId: string | null) => void;
  allPeople: OrgChartPerson[];
  onRemoveMember?: (person: OrgChartRemovablePerson) => void;
  removePending?: boolean;
}

function OrgNode(props: OrgNodeProps) {
  const {
    node,
    editable,
    collapsed,
    onToggle,
    dragId,
    onDragStart,
    onDragEnd,
    onDrop,
    menuFor,
    onOpenMenu,
    onAssign,
    allPeople,
    onRemoveMember,
    removePending,
  } = props;

  const [isOver, setIsOver] = useState(false);
  const hasReports = node.reports.length > 0;
  const isCollapsed = collapsed.has(node.id);
  const isDragging = dragId === node.id;

  return (
    <li className="org-node relative flex flex-col items-center">
      <div
        draggable={editable}
        onDragStart={(e) => {
          if (!editable) return;
          e.stopPropagation();
          onDragStart(node.id);
        }}
        onDragEnd={onDragEnd}
        onDragOver={(e) => {
          if (!editable || !dragId || dragId === node.id) return;
          e.preventDefault();
          setIsOver(true);
        }}
        onDragLeave={() => setIsOver(false)}
        onDrop={(e) => {
          if (!editable) return;
          e.preventDefault();
          e.stopPropagation();
          setIsOver(false);
          onDrop(node.id);
        }}
        className={[
          'relative w-48 rounded-lg border bg-white p-3 shadow-sm transition',
          editable ? 'cursor-grab active:cursor-grabbing' : '',
          isDragging ? 'opacity-50' : '',
          isOver ? 'border-brand-500 ring-2 ring-brand-300' : 'border-gray-200',
        ].join(' ')}
      >
        <div className="flex items-center gap-2">
          <Avatar person={node} size="md" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900">{node.fullName}</p>
            <p className="truncate text-xs text-gray-500">{node.title ?? '—'}</p>
          </div>
        </div>
        {node.department && (
          <span className="mt-2 inline-block rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
            {node.department}
          </span>
        )}

        {editable && (
          <button
            type="button"
            onClick={() => onOpenMenu(menuFor === node.id ? null : node.id)}
            className="absolute right-1 top-1 rounded px-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Editează linia de raportare"
          >
            ⋯
          </button>
        )}

        {onRemoveMember && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveMember({ id: node.id, email: node.email, fullName: node.fullName });
            }}
            disabled={removePending}
            className={`absolute top-1 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 ${
              editable ? 'right-7' : 'right-1'
            }`}
            aria-label="Scoate din proiect"
            title="Scoate din proiect"
          >
            <Trash2 size={14} />
          </button>
        )}

        {editable && menuFor === node.id && (
          <NodeMenu
            node={node}
            allPeople={allPeople}
            onAssign={onAssign}
            onClose={() => onOpenMenu(null)}
          />
        )}

        {hasReports && (
          <button
            type="button"
            onClick={() => onToggle(node.id)}
            className="absolute -bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full border border-gray-300 bg-white px-1.5 text-xs leading-5 text-gray-600 shadow-sm hover:bg-gray-50"
            aria-label={isCollapsed ? 'Extinde ramura' : 'Restrânge ramura'}
            title={isCollapsed ? 'Extinde' : 'Restrânge'}
          >
            {isCollapsed ? `+${countDescendants(node)}` : '−'}
          </button>
        )}
      </div>

      {hasReports && !isCollapsed && (
        <ul className="org-children flex justify-center gap-8">
          {node.reports.map((child) => (
            <OrgNode {...props} key={child.id} node={child} />
          ))}
        </ul>
      )}
    </li>
  );
}

interface NodeMenuProps {
  node: OrgChartNode;
  allPeople: OrgChartPerson[];
  onAssign: (personId: string, managerId: string | null) => void;
  onClose: () => void;
}

function NodeMenu({ node, allPeople, onAssign, onClose }: NodeMenuProps) {
  // Candidate managers: anyone except the person themselves. (The backend
  // rejects cycles, and the UI also blocks self-drop; the picker keeps it
  // simple and lets the server be the final guard.)
  const candidates = allPeople.filter((p) => p.id !== node.id);

  return (
    <div className="absolute left-1/2 top-full z-20 mt-1 w-56 -translate-x-1/2 rounded-lg border border-gray-200 bg-white p-2 text-left shadow-lg">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500">Raportează către</span>
        <button
          type="button"
          onClick={onClose}
          className="rounded px-1 text-gray-400 hover:bg-gray-100"
          aria-label="Închide meniul"
        >
          ✕
        </button>
      </div>
      <Select
        label="Raportează către"
        hideLabel
        size="sm"
        clearable
        searchable
        value={node.managerId ?? ''}
        onChange={(v) => onAssign(node.id, v === '' ? null : v)}
        placeholder="— Fără manager (nivel superior) —"
        options={candidates.map((p) => ({
          value: p.id,
          label: `${p.fullName}${p.title ? ` (${p.title})` : ''}`,
        }))}
        className="w-full"
      />
      {node.managerId && (
        <button
          type="button"
          onClick={() => onAssign(node.id, null)}
          className="mt-2 w-full rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
        >
          Detașează din ierarhie
        </button>
      )}
    </div>
  );
}

function Avatar({ person, size }: { person: OrgChartPerson; size: 'sm' | 'md' }) {
  const dim = size === 'sm' ? 'h-7 w-7 text-[10px]' : 'h-10 w-10 text-xs';
  if (person.avatarUrl) {
    return (
      <img
        src={person.avatarUrl}
        alt={person.fullName}
        className={`${dim} flex-shrink-0 rounded-full object-cover`}
      />
    );
  }
  return (
    <div
      className={`${dim} flex flex-shrink-0 items-center justify-center rounded-full bg-brand-600 font-semibold text-white`}
    >
      {initials(person.fullName)}
    </div>
  );
}

function countDescendants(node: OrgChartNode): number {
  let n = 0;
  for (const r of node.reports) n += 1 + countDescendants(r);
  return n;
}
